<div class="container" [ngClass]="'state-' + conversionState">
  <h1 class="page-title">Convert Classic Release Pipeline</h1>
  <p class="page-description">Transform your classic release pipelines into modern YAML-based Azure DevOps pipelines using AI</p>

  @if (conversionState === 'complete') {
    <div class="start-over-container">
      <button mat-raised-button color="primary" (click)="resetConversion()" class="start-over-button">
        <mat-icon>replay</mat-icon>
        Start Over
      </button>
    </div>
  }

  <div>
    @switch (conversionState) {
      @case ('form') {
        <div>
          <mat-card class="main-card">
            <mat-card-content>
              <form [formGroup]="migrationForm" class="migration-form">
                <div class="section-header">
                  <h3 class="section-title">{{migrationForm.get('devEnabled')?.value ? 'Select Pipelines' : 'Select Pipeline'}}</h3>
                  <p class="section-description">{{migrationForm.get('devEnabled')?.value ? 'Choose classic release pipelines to convert' : 'Choose a classic release pipeline to convert'}}</p>
                </div>
                <mat-form-field appearance="outline" class="full-width pipeline-select" subscriptSizing="dynamic">
                  <input matInput
                    formControlName="rcReleaseDefinition"
                    placeholder="Select a pipeline..."
                    [matAutocomplete]="rcAuto">
                  <mat-autocomplete #rcAuto="matAutocomplete" [displayWith]="displayReleaseName">
                    @for (release of filteredReleaseDefinitions | async; track release) {
                      <mat-option [value]="release">
                        {{release.name}}
                      </mat-option>
                    }
                  </mat-autocomplete>
                  @if (migrationForm.get('rcReleaseDefinition')?.hasError('required')) {
                    <mat-error>
                      Release pipeline is required
                    </mat-error>
                  }
                </mat-form-field>
                <div class="dev-section">
                  <mat-checkbox formControlName="devEnabled" class="dev-checkbox">
                    Convert dev pipeline
                  </mat-checkbox>
                </div>
                @if (migrationForm.get('devEnabled')?.value) {
                  <div class="dev-pipeline-section">
                    <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                      <input matInput
                        formControlName="devReleaseDefinition"
                        placeholder="Select dev pipeline..."
                        [matAutocomplete]="devAuto">
                      <mat-autocomplete #devAuto="matAutocomplete" [displayWith]="displayReleaseName">
                        @for (release of filteredDevReleaseDefinitions | async; track release) {
                          <mat-option [value]="release">
                            {{release.name}}
                          </mat-option>
                        }
                      </mat-autocomplete>
                      @if (migrationForm.get('devReleaseDefinition')?.hasError('required')) {
                        <mat-error>
                          Dev pipeline is required
                        </mat-error>
                      }
                    </mat-form-field>
                  </div>
                }
                <button
                  mat-raised-button
                  color="primary"
                  class="convert-button"
                  [disabled]="!canConvert()"
                  (click)="convertToYaml()">
                  <mat-icon>play_arrow</mat-icon>
                  Convert to YAML
                </button>
              </form>
            </mat-card-content>
          </mat-card>
        </div>
      }
      @case ('in_progress') {
        <div>
          <mat-card class="main-card">
            <mat-card-content>
              <div class="progress-container">
                <h3 class="progress-title">{{ isRefinement() ? 'Refinement in Progress...' : 'Conversion in Progress...' }}</h3>
                <p class="progress-description">{{ isRefinement() ? 'Please wait while we refine the previous conversion. This may take several minutes.' : 'Please wait while we convert the following pipeline' + (migrationForm.get('devEnabled')?.value ? 's' : '') + '. This may take several minutes.' }}</p>
                <div class="converting-pipelines">
                  <ul class="pipeline-list">
                    <li class="pipeline-item">
                      <mat-icon class="pipeline-icon">account_tree</mat-icon>
                      {{ migrationForm.get('rcReleaseDefinition')?.value?.name }}
                    </li>
                    @if (migrationForm.get('devEnabled')?.value) {
                      <li class="pipeline-item">
                        <mat-icon class="pipeline-icon">account_tree</mat-icon>
                        {{ migrationForm.get('devReleaseDefinition')?.value?.name }}
                      </li>
                    }
                  </ul>
                </div>
                <ul class="progress-steps">
                  @for (step of conversionSteps; track step) {
                    <li class="progress-step" [ngClass]="'status-' + step.status.toLowerCase().replace(' ', '-')">
                      <mat-icon class="step-icon">{{step.icon}}</mat-icon>
                      <span class="step-name">{{step.displayName}}</span>
                    </li>
                  }
                </ul>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      }
      @case ('failed') {
        <div>
          <mat-card class="main-card">
            <mat-card-content>
              <div class="error-container">
                <mat-icon class="error-icon">error</mat-icon>
                <h3 class="error-title">Conversion Failed</h3>
                <p class="error-description">The conversion process encountered an error and could not be completed.</p>
                @if (conversionError) {
                  <div class="error-details">
                    <p class="error-message">{{conversionError}}</p>
                  </div>
                }
                <div class="error-actions">
                  @if (lastFailedOperation) {
                    <button mat-raised-button color="primary" class="retry-button"
                      (click)="retryLastOperation()">
                      <mat-icon>refresh</mat-icon>
                      Try Again
                    </button>
                  }
                  <button mat-stroked-button class="start-over-button" (click)="resetConversion()">
                    <mat-icon>replay</mat-icon>
                    Start Over
                  </button>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      }
      @case ('complete') {
        <div>
          <div class="results-container">
            <div class="results-grid">
              <mat-card class="main-card files-card">
                <mat-card-content>
                  <h3 class="section-title">Generated Files</h3>
                  <p class="section-description">Review the converted YAML pipeline structure</p>
                  <mat-tree #tree [dataSource]="treeDataSource" [childrenAccessor]="childrenAccessor" class="file-tree">
                    <mat-nested-tree-node *matTreeNodeDef="let node">
                      <div class="mat-tree-node" [ngClass]="{ 'selected-node': selectedFile === node }">
                        <div class="file-node" (click)="selectFile(node); $event.stopPropagation()">
                          <button mat-icon-button disabled></button>
                          <mat-icon class="node-icon" [ngClass]="{ 'has-notes': node.notes && node.type === 'file' }">description</mat-icon>
                          {{node.name}}
                        </div>
                      </div>
                    </mat-nested-tree-node>
                    <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild" matTreeNodeToggle>
                      <div class="mat-tree-node">
                        <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                          <mat-icon class="mat-icon-rtl-mirror">
                            {{tree.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
                          </mat-icon>
                        </button>
                        <div class="file-node">
                          <mat-icon class="node-icon">folder</mat-icon>
                          {{node.name}}
                        </div>
                      </div>
                      <fieldset [class.tree-invisible]="!tree.isExpanded(node)">
                        <ng-container matTreeNodeOutlet></ng-container>
                      </fieldset>
                    </mat-nested-tree-node>
                  </mat-tree>
                  <div class="action-buttons">
                    <button mat-raised-button color="primary" class="pr-button" [disabled]="treeDataSource.length === 0" (click)="createPullRequest()">
                      <mat-icon>merge</mat-icon>
                      Create Pull Request
                    </button>
                    @if (showRequestChangesButton) {
                      <button mat-stroked-button class="changes-button" (click)="openRequestChangesDialog()">
                        <mat-icon>edit</mat-icon>
                        Request Changes
                      </button>
                    }
                  </div>
                </mat-card-content>
              </mat-card>
              <mat-card class="main-card preview-card">
                <mat-card-content>
                  <h3 class="section-title">File Preview</h3>
                  @if (!selectedFile) {
                    <p class="section-description">Select a file to preview its contents</p>
                  }
                  @if (selectedFile) {
                    <div class="preview-container">
                      <div class="preview-header">{{selectedFile.name}}</div>
                      <pre class="preview-content"><code>{{selectedFile.content}}</code></pre>
                    </div>
                  }
                  @if (!selectedFile) {
                    <div class="empty-preview">
                      <mat-icon class="empty-icon">description</mat-icon>
                      <p>Select a file from the tree to view its contents</p>
                    </div>
                  }
                </mat-card-content>
              </mat-card>
            </div>
            @if (selectedFileNotes) {
              <div class="notes-section">
                <mat-card class="main-card notes-card">
                  <mat-card-content>
                    <h3 class="section-title">Notes for {{selectedFile?.name}}</h3>
                    <div class="notes-content">
                      <markdown [data]="selectedFileNotes" [inline]="true"></markdown>
                    </div>
                  </mat-card-content>
                </mat-card>
              </div>
            }
          </div>
        </div>
      }
      @case ('pr_complete') {
        <div>
          <mat-card class="main-card">
            <mat-card-content>
              <div class="pr-success-container">
                <mat-icon class="success-icon">check_circle</mat-icon>
                <h3 class="pr-success-title">Pull Request Created!</h3>
                <p class="pr-success-description">Your pull request has been successfully created. You can view it at the link below.</p>
                <a mat-raised-button color="primary" [href]="pullRequestUrl" target="_blank">
                  <mat-icon>open_in_new</mat-icon>
                  View Pull Request
                </a>
                <div class="converted-pipelines">
                  <h4>Converted Pipelines:</h4>
                  <ul>
                    <li>{{ migrationForm.get('rcReleaseDefinition')?.value?.name }}</li>
                    @if (migrationForm.get('devEnabled')?.value) {
                      <li>{{ migrationForm.get('devReleaseDefinition')?.value?.name }}</li>
                    }
                  </ul>
                </div>
                <button mat-stroked-button (click)="resetConversion()" class="start-over-button">
                  <mat-icon>replay</mat-icon>
                  Start New Conversion
                </button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      }
    }
  </div>
</div>
